/**
 * Cây Sản phẩm → Hợp phần của lệnh sản xuất (plan-lsx-recursive-structure-v1, Giai đoạn 3).
 * Dùng chung cho view (tab 1–4) và form: sắp hợp phần cha trước con, theo dòng sản phẩm của lệnh.
 */

export interface ComponentNode {
  id: string;
  name: string;
  depth: number;
  /** Đường dẫn tên từ gốc, ví dụ "Hộp › Nắp › Lót" */
  path: string;
  sourceId: string | null;
  component: any;
}

/** Hợp phần còn hiệu lực của lệnh */
export const activeComponents = (components: any[] | undefined | null): any[] =>
  (components || []).filter((c) => c && !c.isDeleted);

/** Lệnh có nhiều hợp phần hoặc có hợp phần lồng cấp → hiển thị theo cây; còn lại giữ bảng phẳng */
export function isComponentTreeMode(components: any[] | undefined | null): boolean {
  const list = activeComponents(components);
  return list.length > 1 || list.some((c) => c.parentComponentId);
}

/**
 * Sắp hợp phần theo cây: nhóm theo dòng sản phẩm (theo thứ tự `sourceOrder`), trong mỗi dòng là
 * duyệt sâu cha → con theo sortOrder. Hợp phần trỏ tới cha không tồn tại được coi là gốc; vòng lặp bị cắt.
 */
export function orderComponentTree(components: any[] | undefined | null, sourceOrder: string[] = []): ComponentNode[] {
  const list = activeComponents(components);
  const byId = new Map(list.map((c) => [c.id, c]));
  const children = new Map<string, any[]>();
  const roots: any[] = [];
  for (const c of list) {
    const parentId = c.parentComponentId && byId.has(c.parentComponentId) && c.parentComponentId !== c.id ? c.parentComponentId : null;
    if (parentId) {
      if (!children.has(parentId)) children.set(parentId, []);
      children.get(parentId)!.push(c);
    } else {
      roots.push(c);
    }
  }
  const bySort = (a: any, b: any) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
  const sourceRank = (c: any) => {
    const i = c.productionOrderSourceId ? sourceOrder.indexOf(c.productionOrderSourceId) : -1;
    return i === -1 ? Number.MAX_SAFE_INTEGER : i;
  };
  roots.sort((a, b) => sourceRank(a) - sourceRank(b) || bySort(a, b));

  const out: ComponentNode[] = [];
  const seen = new Set<string>();
  const walk = (c: any, depth: number, parentPath: string, rootSource: string | null) => {
    if (seen.has(c.id)) return;
    seen.add(c.id);
    const name = c.componentName || "Hợp phần";
    const path = parentPath ? `${parentPath} › ${name}` : name;
    const sourceId = c.productionOrderSourceId ?? rootSource;
    out.push({ id: c.id, name, depth, path, sourceId, component: c });
    for (const child of (children.get(c.id) || []).sort(bySort)) walk(child, depth + 1, path, sourceId);
  };
  for (const r of roots) walk(r, 0, "", r.productionOrderSourceId ?? null);
  // Phần còn lại (chỉ xảy ra khi dữ liệu có vòng lặp) đưa về cấp gốc để không mất dòng
  for (const c of list) if (!seen.has(c.id)) walk(c, 0, "", c.productionOrderSourceId ?? null);
  return out;
}

/** Vị trí của hợp phần trong cây (dùng để sắp dòng công đoạn/khuôn/vật tư); không thuộc hợp phần nào → cuối */
export function componentRankMap(nodes: ComponentNode[]): (componentId: string | null | undefined) => number {
  const rank = new Map(nodes.map((n, i) => [n.id, i]));
  return (componentId) => (componentId && rank.has(componentId) ? rank.get(componentId)! : nodes.length);
}
